import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from "@angular/router";
import { Subscription, filter } from "rxjs";
import {
  AltiContext,
  AltiPage,
  ExperienceAltiService,
} from "../services/experience-alti.service";
import { ExperienceDataService } from "../services/experience-data.service";
import {
  ExperienceTheme,
  ExperienceThemeService,
} from "../services/experience-theme.service";
import { AltiRailComponent } from "./alti-rail/alti-rail.component";
import {
  CommandPaletteComponent,
  PaletteItem,
} from "./command-palette/command-palette.component";

const STORAGE_ALTI = "cimalti-alti";

@Component({
  selector: "app-exp-shell",
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [RouterModule, AltiRailComponent, CommandPaletteComponent],
  templateUrl: "./experience-shell.component.html",
})
export class ExperienceShellComponent implements OnInit, OnDestroy {
  portfolioId: number | null = null;
  theme: ExperienceTheme = "dark";
  altiState: "open" | "collapsed" = "open";

  portfolioName = "";
  currentPage: AltiPage = "optimize";
  paletteOpen = false;

  toasts: Array<{ id: number; text: string }> = [];
  private toastId = 0;

  paletteItems: PaletteItem[] = [];
  context: AltiContext = { page: "optimize" };

  private subs = new Subscription();

  constructor(
    public themeService: ExperienceThemeService,
    public dataService: ExperienceDataService,
    public altiService: ExperienceAltiService,
    public route: ActivatedRoute,
    public router: Router,
  ) {}

  ngOnInit(): void {
    try {
      const raw = localStorage.getItem(STORAGE_ALTI);
      if (raw === "open" || raw === "collapsed") this.altiState = raw;
    } catch {
      /* noop */
    }

    this.subs.add(
      this.themeService.current$.subscribe((t) => (this.theme = t)),
    );

    this.subs.add(
      this.route.paramMap.subscribe((pm) => {
        const id = Number(pm.get("id"));
        if (id && id !== this.portfolioId) {
          this.portfolioId = id;
          this.dataService.load(id);
        }
      }),
    );

    this.subs.add(
      this.dataService.portfolio$.subscribe((pf) => {
        if (pf) {
          this.portfolioName = (pf as any).name || "Portfolio";
          this.context = { ...this.context, page: this.currentPage };
        }
      }),
    );

    this.updateCurrentPage(this.router.url);
    this.subs.add(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe((e: any) =>
          this.updateCurrentPage(e.urlAfterRedirects || e.url),
        ),
    );

    this.paletteItems = this.buildPaletteItems();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  setAltiContext(context: Partial<AltiContext>): void {
    this.context = { ...this.context, ...context, page: this.currentPage };
  }

  private updateCurrentPage(url: string): void {
    if (/\/edit(\?|$|\/)/.test(url)) this.currentPage = "edit";
    else if (/\/backtest(\?|$|\/)/.test(url)) this.currentPage = "backtest";
    else if (/\/risk(\?|$|\/)/.test(url)) this.currentPage = "risk";
    else this.currentPage = "optimize";
    this.context = { ...this.context, page: this.currentPage };
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  toggleAlti(): void {
    this.altiState = this.altiState === "open" ? "collapsed" : "open";
    try {
      localStorage.setItem(STORAGE_ALTI, this.altiState);
    } catch {
      /* noop */
    }
  }

  openPalette(): void {
    this.paletteItems = this.buildPaletteItems();
    this.paletteOpen = true;
  }

  closePalette(): void {
    this.paletteOpen = false;
  }

  pickPalette(item: PaletteItem): void {
    this.paletteOpen = false;
    if (item.kind === "nav" && item.route) {
      this.router.navigate(item.route);
    } else if (item.kind === "ask") {
      this.altiService.ask(
        this.currentPage,
        item.label.replace(/^Ask Alti:\s*/, ""),
      );
      if (this.altiState === "collapsed") this.toggleAlti();
    } else if (item.kind === "action") {
      this.runAction(item.action || "");
    }
  }

  palleteAskAlti(q: string): void {
    this.altiService.ask(this.currentPage, q);
    if (this.altiState === "collapsed") this.toggleAlti();
  }

  private runAction(action: string): void {
    switch (action) {
      case "optimize":
        this.toast("Optimization queued");
        break;
      case "toggle-theme":
        this.toggleTheme();
        break;
      case "toggle-alti":
        this.toggleAlti();
        break;
      default:
        this.toast("Action complete");
    }
  }

  toast(text: string): void {
    const id = ++this.toastId;
    this.toasts = [...this.toasts, { id, text }];
    setTimeout(() => {
      this.toasts = this.toasts.filter((t) => t.id !== id);
    }, 2200);
  }

  private buildPaletteItems(): PaletteItem[] {
    const id = this.portfolioId;
    const items: PaletteItem[] = [];
    if (id) {
      items.push(
        {
          id: "nav-edit",
          kind: "nav",
          label: "Go to Edit",
          hint: "Portfolio settings",
          route: ["/app/home-experience/portfolio", id, "edit"],
        },
        {
          id: "nav-backtest",
          kind: "nav",
          label: "Go to Backtest",
          hint: "Historical performance",
          route: ["/app/home-experience/portfolio", id, "backtest"],
        },
        {
          id: "nav-optimize",
          kind: "nav",
          label: "Go to Optimize",
          hint: "Allocation & views",
          route: ["/app/home-experience/portfolio", id, "optimize"],
        },
        {
          id: "nav-risk",
          kind: "nav",
          label: "Go to Risk",
          hint: "Hot spots & drawdown",
          route: ["/app/home-experience/portfolio", id, "risk"],
        },
      );
    }
    items.push(
      {
        id: "nav-home",
        kind: "nav",
        label: "Back to portfolios",
        hint: "Home",
        route: ["/app/home-experience"],
      },
      {
        id: "act-opt",
        kind: "action",
        label: "Run optimization",
        action: "optimize",
      },
      {
        id: "act-theme",
        kind: "action",
        label: "Toggle theme",
        action: "toggle-theme",
      },
      {
        id: "act-alti",
        kind: "action",
        label: "Toggle Alti rail",
        action: "toggle-alti",
      },
      {
        id: "ask-summary",
        kind: "ask",
        label: "Ask Alti: Summarize this page",
        hint: "ask",
      },
      {
        id: "ask-risk",
        kind: "ask",
        label: "Ask Alti: What are my risk hot spots?",
        hint: "ask",
      },
      {
        id: "ask-tilt",
        kind: "ask",
        label: "Ask Alti: Propose a tilt",
        hint: "ask",
      },
    );
    return items;
  }
}
